import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["toggleButton"]
  static values = {
    mode: { type: String, default: "hours" }
  }

  connect() {
    // Listen for recalculation events
    window.addEventListener('matrix:recalculate', this.recalculate.bind(this))

    // Load saved mode from localStorage
    const savedMode = localStorage.getItem('matrix_view_mode') || 'hours'
    this.modeValue = savedMode
    this.updateToggleButton()
  }

  disconnect() {
    window.removeEventListener('matrix:recalculate', this.recalculate.bind(this))
  }

  toggleMode() {
    this.modeValue = this.modeValue === 'hours' ? 'days' : 'hours'
    localStorage.setItem('matrix_view_mode', this.modeValue)
    this.updateToggleButton()

    // Dispatch event to notify cells about mode change
    window.dispatchEvent(new CustomEvent('matrix:mode-changed', {
      detail: { mode: this.modeValue }
    }))
  }

  updateToggleButton() {
    if (this.hasToggleButtonTarget) {
      this.toggleButtonTarget.textContent = this.modeValue === 'hours' ? 'Hours' : 'Days'

      // Update button styling
      if (this.modeValue === 'days') {
        this.toggleButtonTarget.classList.remove('bg-blue-600', 'hover:bg-blue-700')
        this.toggleButtonTarget.classList.add('bg-green-600', 'hover:bg-green-700')
      } else {
        this.toggleButtonTarget.classList.remove('bg-green-600', 'hover:bg-green-700')
        this.toggleButtonTarget.classList.add('bg-blue-600', 'hover:bg-blue-700')
      }
    }
  }

  get currentMode() {
    return this.modeValue
  }

  recalculate(event) {
    const data = event.detail

    // Update user totals
    if (data.user_totals) {
      this.updateUserTotals(data.assignment.user_id, data.user_totals)
    }

    // Update project totals
    if (data.project_totals) {
      this.updateProjectTotals(data.assignment.project_id, data.project_totals)
    }
  }

  updateUserTotals(userId, totals) {
    // Update assigned total
    const totalCell = document.querySelector(`[data-user-total="${userId}"]`)
    if (totalCell) {
      totalCell.textContent = totals.total_assigned
    }

    // Update available hours with color
    const availableCell = document.querySelector(`[data-user-available="${userId}"]`)
    if (availableCell) {
      availableCell.textContent = parseFloat(totals.available).toFixed(1)

      // Update color based on status
      availableCell.classList.remove('text-green-600', 'text-yellow-600', 'text-red-600', 'font-bold')

      switch(totals.status) {
        case 'over_allocated':
          availableCell.classList.add('text-red-600', 'font-bold')
          break
        case 'under_allocated':
          availableCell.classList.add('text-yellow-600')
          break
        case 'balanced':
          availableCell.classList.add('text-green-600')
          break
      }
    }
  }

  updateProjectTotals(projectId, totals) {
    const totalCell = document.querySelector(`[data-project-total="${projectId}"]`)
    if (totalCell) {
      // Try to find the new structure with separate required/assigned targets
      const requiredSpan = totalCell.querySelector('[data-project-hours-editor-target="required"]')
      const assignedSpan = totalCell.querySelector('[data-project-hours-editor-target="assigned"]')
      const displayDiv = totalCell.querySelector('[data-project-hours-editor-target="display"]')

      if (requiredSpan && assignedSpan && displayDiv) {
        // New editable structure
        requiredSpan.textContent = totals.required
        assignedSpan.textContent = totals.total_assigned

        // Update color based on difference
        displayDiv.classList.remove('text-green-600', 'text-yellow-600', 'text-red-600',
                                     'bg-green-100', 'bg-yellow-100', 'bg-red-100')

        const diff = totals.difference

        if (diff < -5) {
          displayDiv.classList.add('text-red-600', 'bg-red-100')
        } else if (diff > 5) {
          displayDiv.classList.add('text-yellow-600', 'bg-yellow-100')
        } else {
          displayDiv.classList.add('text-green-600', 'bg-green-100')
        }
      } else {
        // Fallback to old structure
        const span = totalCell.querySelector('span')
        if (span) {
          span.textContent = `${totals.required} | ${totals.total_assigned}`

          // Update color based on difference
          span.classList.remove('text-green-600', 'text-yellow-600', 'text-red-600')

          const diff = totals.difference

          if (diff < -5) {
            span.classList.add('text-red-600')
          } else if (diff > 5) {
            span.classList.add('text-yellow-600')
          } else {
            span.classList.add('text-green-600')
          }
        }
      }
    }
  }
}
